import { useState, useEffect } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { Box, Breadcrumbs } from "@mui/material";
import Grid from "@mui/material/Grid";
import SearchIcon from "@mui/icons-material/Search";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import DeleteIcon from "@mui/icons-material/Delete";
import TextField from "@mui/material/TextField";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import GridTable from "../../../../components/GridTable";
import Dialog from "../../../../components/Dialog";
import Loader from "../../../../components/Loader";
import Button from "../../../../components/Button";
import ActionMenu from "../../../../components/ActionMenu";
import { successMessage, errorMessage } from "../../../../components/Toaster";
import { getCompanyVehicles, createVehicleTag, deleteVehicleTag } from "./api";
import { getErrorMessage } from "../../../../utils/error";
import { VISO_SUPER_ADMIN } from "../../../../constants";

const VehicleTag = () => {
  const location = useLocation();
  const { company_id, yard_id } = useParams();
  const company_name = location.state?.company_name || "Company";
  const yard_name = location.state?.yard_name || "Plant";

  const role = localStorage.getItem("role_name");
  const isVisoSuperAdmin = role === VISO_SUPER_ADMIN;
  const companyBreadcrumbPath = isVisoSuperAdmin ? "/company" : `/company/${company_id}/yards`;

  const [vehicles, setVehicles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [loading, setLoading] = useState({
    create: false,
    fetch: false,
    delete: false,
  });

  const validationSchema = Yup.object().shape({
    vehicleNumber: Yup.string()
      .required("Vehicle Number is required")
      .max(20, "Vehicle Number must be at most 20 characters"),
    tagId: Yup.string()
      .required("Tag ID is required")
      .max(64, "Tag ID must be at most 64 characters"),
    vehicleType: Yup.string()
      .required("Vehicle Type is required")
      .max(50, "Vehicle Type must be at most 50 characters"),
  });

  const columns = [
    {
      field: "vehicle_number",
      headerName: "Vehicle Number",
      headerClassName: "table-header",
      flex: 1,
    },
    {
      field: "tag_id",
      headerName: "Tag ID",
      headerClassName: "table-header",
      flex: 1,
    },
    {
      field: "vehicle_type",
      headerName: "Vehicle Type",
      headerClassName: "table-header",
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      headerClassName: "table-header",
      renderCell: (params) => (
        <ActionMenu
          params={params}
          actions={[
            {
              icon: <DeleteIcon color="error" />,
              tooltip: "Delete",
              onClick: handleDelete,
            },
          ]}
        />
      ),
      sortable: false,
      filterable: false,
      flex: 1,
    },
  ];

  useEffect(() => {
    const fetchVehicles = async () => {
      setLoading((prev) => ({ ...prev, fetch: true }));
      try {
        const response = await getCompanyVehicles(company_id || "");
        if (response.status) {
          setVehicles(response.data || []);
        } else {
          errorMessage(response.message || "Failed to fetch vehicles.");
        }
      } catch (error) {
        errorMessage(getErrorMessage(error) || "Error fetching vehicles");
      } finally {
        setLoading((prev) => ({ ...prev, fetch: false }));
      }
    };

    if (company_id) fetchVehicles();
  }, [company_id]);

  const openDialog = () => setDialogOpen(true);

  const closeDialog = (resetForm) => {
    resetForm();
    setDialogOpen(false);
  };

  const handleDelete = (params) => {
    setItemToDelete(params);
    setDeleteOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteOpen(false);
    setItemToDelete(null);
  };

  const confirmDialog = async (values, { resetForm }) => {
    const success = await handleCreate(values);
    if (success) closeDialog(resetForm);
  };

  const handleCreate = async (values) => {
    setLoading((prev) => ({ ...prev, create: true }));
    try {
      const payload = {
        company_id: company_id || "",
        vehicle_number: values.vehicleNumber.toUpperCase().replace(/\s+/g, ""),
        tag_id: values.tagId.trim(),
        vehicle_type: values.vehicleType.trim(),
      };

      const response = await createVehicleTag(payload);
      if (response?.status) {
        setVehicles((prev) => [
          {
            vehicle_id: response?.data?.vehicle_id,
            vehicle_number: payload.vehicle_number,
            tag_id: payload.tag_id,
            vehicle_type: payload.vehicle_type,
          },
          ...(Array.isArray(prev) ? prev : []),
        ]);
        successMessage(response.message || "Vehicle created successfully!");
        return true;
      }
      errorMessage(response?.message || "Failed to create vehicle. Please try again.");
      return false;
    } catch (error) {
      errorMessage(getErrorMessage(error) || "An unexpected error occurred. Please try again.");
      return false;
    } finally {
      setLoading((prev) => ({ ...prev, create: false }));
    }
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    setLoading((prev) => ({ ...prev, delete: true }));
    try {
      const response = await deleteVehicleTag(company_id, itemToDelete.row.vehicle_id);
      if (response.status) {
        setVehicles((prev) => prev.filter((v) => v.vehicle_id !== itemToDelete.row.vehicle_id));
        successMessage(response.message || "Vehicle deleted successfully!");
      } else {
        errorMessage(response.message || "Failed to delete vehicle. Please try again.");
      }
    } catch (error) {
      errorMessage(getErrorMessage(error) || "An unexpected error occurred. Please try again.");
    } finally {
      setLoading((prev) => ({ ...prev, delete: false }));
      setDeleteOpen(false);
      setItemToDelete(null);
    }
  };

  return (
    <>
      {loading.fetch ? (
        <Loader color="primary" size={40} />
      ) : (
        <Box style={{ width: "100%", height: "50%" }}>
          <main className="m-a-50">
            <div style={{ display: "flex", alignItems: "center", marginBottom: "30px" }}>
              <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                style={{ fontSize: "18px", fontWeight: "bold" }}
              >
                {isVisoSuperAdmin ? (
                  <Link color="primary" to="/company">
                    Company
                  </Link>
                ) : (
                  <span style={{ color: "inherit", cursor: "default" }}>Company</span>
                )}
                <Link color="primary" to={companyBreadcrumbPath}>
                  {company_name}
                </Link>
                <Link color="primary" to={`/company/${company_id}/yards`} state={{ company_name }}>
                  Plants
                </Link>
                <Link color="primary" to={`/company/${company_id}/yards`} state={{ company_name }}>
                  {yard_name}
                </Link>
                <Link
                  color="primary"
                  to={`/company/${company_id}/yards/${yard_id}/vehicles`}
                  state={{ company_name, yard_name }}
                >
                  Vehicles
                </Link>
              </Breadcrumbs>
            </div>

            <div className="d-flex justify-end">
              <div className="d-flex justify-between w-100">
                <TextField
                  className="w-25"
                  label="Search"
                  variant="outlined"
                  value={searchQuery}
                  size="small"
                  startIcon={<SearchIcon />}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <Button label="Add" variant="contained" color="primary" onClick={openDialog} />
              </div>
            </div>

            <div style={{ height: "100%", width: "100%" }} className="ag-theme-alpine m-t-20">
              <GridTable
                rows={vehicles}
                columns={columns}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                getRowId={(row) => row.vehicle_id}
                density="standard"
              />
            </div>

            <Formik
              initialValues={{
                vehicleNumber: "",
                tagId: "",
                vehicleType: "",
              }}
              validationSchema={validationSchema}
              onSubmit={confirmDialog}
            >
              {({ values, errors, touched, handleChange, handleSubmit, resetForm, setFieldValue }) => (
                <Dialog
                  open={isDialogOpen}
                  onClose={() => closeDialog(resetForm)}
                  onConfirm={handleSubmit}
                  title={!loading.create ? "Add New Vehicle" : ""}
                  confirmButtonText={!loading.create ? "Add" : ""}
                  cancelButtonText={!loading.create ? "Cancel" : ""}
                  icon={<LocalShippingIcon />}
                  message={
                    <Box padding={4}>
                      {loading.create ? (
                        <Loader color="primary" size={40} />
                      ) : (
                        <Form>
                          <Grid container spacing={2}>
                            <Grid size={4}>
                              <TextField
                                id="vehicleNumber"
                                name="vehicleNumber"
                                label="Vehicle Number"
                                placeholder="e.g. HR39AB1234"
                                required
                                fullWidth
                                value={values.vehicleNumber}
                                onChange={(e) => setFieldValue("vehicleNumber", e.target.value.toUpperCase())}
                                error={touched.vehicleNumber && Boolean(errors.vehicleNumber)}
                                helperText={touched.vehicleNumber && errors.vehicleNumber}
                              />
                            </Grid>
                            <Grid size={4}>
                              <TextField
                                id="tagId"
                                name="tagId"
                                label="Tag ID"
                                placeholder="e.g. TAG1001"
                                required
                                fullWidth
                                value={values.tagId}
                                onChange={handleChange}
                                error={touched.tagId && Boolean(errors.tagId)}
                                helperText={touched.tagId && errors.tagId}
                              />
                            </Grid>
                            <Grid size={4}>
                              <TextField
                                id="vehicleType"
                                name="vehicleType"
                                label="Vehicle Type"
                                placeholder="e.g. Truck"
                                required
                                fullWidth
                                value={values.vehicleType}
                                onChange={handleChange}
                                error={touched.vehicleType && Boolean(errors.vehicleType)}
                                helperText={touched.vehicleType && errors.vehicleType}
                              />
                            </Grid>
                          </Grid>
                        </Form>
                      )}
                    </Box>
                  }
                />
              )}
            </Formik>

            <Dialog
              open={deleteOpen}
              onClose={handleDeleteCancel}
              onConfirm={handleDeleteConfirm}
              title={!loading.delete ? "Delete Vehicle" : ""}
              confirmButtonText={!loading.delete ? "Confirm" : ""}
              cancelButtonText={!loading.delete ? "Cancel" : ""}
              icon={<DeleteIcon />}
              message={
                <Box padding={2}>
                  {loading.delete ? (
                    <Loader color="primary" size={40} />
                  ) : (
                    `Are you sure you want to delete vehicle ${itemToDelete?.row?.vehicle_number || ""}?`
                  )}
                </Box>
              }
            />
          </main>
        </Box>
      )}
    </>
  );
};

export default VehicleTag;